/**
 * PostHog — product analytics only (no payment amounts, no billing payloads, no DB dumps).
 *
 * Env (CRA):
 *   REACT_APP_POSTHOG_KEY   — project API key (phc_…). Omit to disable PostHog entirely.
 *   REACT_APP_POSTHOG_HOST  — optional; default US cloud ingest: https://us.i.posthog.com
 *   REACT_APP_ANALYTICS_ENV — optional label on every event (e.g. local, staging, production).
 *                             If unset: "development" when NODE_ENV is development, else "production".
 *
 * We keep autocapture and session replay OFF so clicks on pay/checkout are not scraped.
 * Use PostHog for paths, funnels, and coarse custom events only.
 */

import posthog from 'posthog-js';

const _rawKey = (process.env.REACT_APP_POSTHOG_KEY || '').trim();
/** CRA inlines env at build time; treat placeholder / typo as missing. */
const KEY =
  _rawKey &&
  _rawKey !== 'undefined' &&
  !_rawKey.startsWith('your_') &&
  !_rawKey.startsWith('REPLACE')
    ? _rawKey
    : '';
const HOST = (process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com').trim();
const DEBUG = String(process.env.REACT_APP_POSTHOG_DEBUG || '').trim() === '1';

/** Sent on every capture + identify so PostHog filters (e.g. milan_env ≠ production) separate dev from prod. */
const ANALYTICS_ENV = (() => {
  const raw = (process.env.REACT_APP_ANALYTICS_ENV || '').trim();
  if (raw && raw !== 'undefined') return raw.slice(0, 64);
  return process.env.NODE_ENV === 'development' ? 'development' : 'production';
})();

let initialized = false;

export function isPosthogConfigured() {
  return !!KEY;
}

export function initPosthog() {
  if (typeof window === 'undefined' || !KEY || initialized) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    /** Fewer round-trips while debugging; capture events do not depend on flags. */
    advanced_disable_feature_flags: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: false,
    debug: DEBUG,
    // Safer defaults: no heatmaps / web vitals unless you turn them on in PostHog UI
    loaded: (ph) => {
      try {
        ph.register({ milan_env: ANALYTICS_ENV });
      } catch {
        /* ignore */
      }
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info(
          '[PostHog] ready — milan_env:',
          ANALYTICS_ENV,
          '— events POST to',
          HOST,
          '(filter Network by “i.posthog” or “batch”, not only “posthog”)'
        );
      }
    },
  });
  initialized = true;
}

export function resetPosthog() {
  if (!initialized || !KEY) return;
  try {
    posthog.reset();
    posthog.register({ milan_env: ANALYTICS_ENV });
  } catch {
    /* ignore */
  }
}

/**
 * Opaque workspace user id (Mongo-style id from API) + coarse role. Never pass email here.
 */
export function identifyBrandCreator(userId, userType) {
  if (!initialized || !KEY) return;
  const id = String(userId || '').trim();
  if (!id) return;
  const ut = String(userType || 'brand').toLowerCase();
  const role = ut === 'creator' ? 'creator' : 'brand';
  posthog.identify(id, { user_type: role, milan_env: ANALYTICS_ENV });
}

export function identifyAdmin(adminId) {
  if (!initialized || !KEY) return;
  const id = String(adminId || '').trim();
  if (!id) return;
  posthog.identify(`admin_${id}`, { user_type: 'milan_admin', milan_env: ANALYTICS_ENV });
}

/** Re-attach identity on full page reload when tokens already exist. */
export function identifyFromStoredSession() {
  if (!initialized || !KEY) return;
  try {
    if (localStorage.getItem('adminToken') && localStorage.getItem('adminId')) {
      identifyAdmin(localStorage.getItem('adminId'));
      return;
    }
    if (localStorage.getItem('token') && localStorage.getItem('userId')) {
      identifyBrandCreator(localStorage.getItem('userId'), localStorage.getItem('userType'));
    }
  } catch {
    /* ignore */
  }
}

/** Pathname-only pageviews (no query string — avoids leaking tokens in URLs). */
export function capturePageview(pathname) {
  if (!initialized || !KEY) return;
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!path) return;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  posthog.capture('$pageview', {
    $current_url: origin ? `${origin}${path}` : path,
    path,
  });
}

/** Funnel: completed signup (no PII). */
export function captureSignupCompleted(method, userType) {
  if (!initialized || !KEY) return;
  const ut = userType === 'creator' ? 'creator' : 'brand';
  const m = method === 'google' ? 'google' : 'email';
  posthog.capture('signup_completed', { method: m, user_type: ut });
}

/**
 * Performance Hub workspace tab (no billing tab — avoids surfacing paywall analytics next to money flows).
 */
export function capturePerformanceHubTab(tab) {
  if (!initialized || !KEY) return;
  const t = String(tab || '').trim();
  if (!t || t === 'billing') return;
  posthog.capture('performance_hub_tab', { tab: t });
}
